import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Select, Spin, Tooltip, message } from "antd";
import { fetchUserMonitorList, fetchUserMonitorLookups } from "./user-monitor-api";
import UserMonitorCard from "./user-monitor-card";
import UserMonitorComboFilter from "./user-monitor-combo-filter";
import UserMonitorDetailsDrawer from "./user-monitor-details-drawer";
import {
  defaultPeriod,
  getLawFlag,
  labelFromMap,
  loadStoredFilters,
  normalizeListRow,
  normalizeLookups,
  pick,
  saveFilters
} from "./user-monitor-utils";
import "./user-monitors.css";

const PAGE_SIZE = 50;
const SCROLL_LOAD_GAP = 80;
const SEARCH_DEBOUNCE_MS = 350;

const SORT_FIELDS = [
  { value: "fdatetime", label: "Время" },
  { value: "fdate", label: "Дата" },
  { value: "userName", label: "Пользователь" },
  { value: "vid", label: "Событие" },
  { value: "idCat", label: "Категория" },
  { value: "objZagName", label: "Объект" },
  { value: "objCntName", label: "Уточнение" },
  { value: "komment", label: "Комментарий" },
  { value: "idReg", label: "Номер" }
];

const SORT_OPTIONS = SORT_FIELDS.flatMap((f) => [
  {
    value: `${f.value}:asc`,
    label: (
      <span className="user-monitors__sort-opt">
        <span>{f.label}</span>
        <span className="user-monitors__sort-dir">↑</span>
      </span>
    )
  },
  {
    value: `${f.value}:desc`,
    label: (
      <span className="user-monitors__sort-opt">
        <span>{f.label}</span>
        <span className="user-monitors__sort-dir">↓</span>
      </span>
    )
  }
]);

const normalizePage = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(pick(data, "items", "Items"))
      ? pick(data, "items", "Items")
      : [];
  const hasMore = Array.isArray(data)
    ? items.length >= PAGE_SIZE
    : Boolean(pick(data, "hasMore", "HasMore"));
  return {
    items: items.map(normalizeListRow).filter((r) => r.idReg != null),
    hasMore
  };
};

/**
 * Журнал действий пользователей. Delphi: TFrmJurnReg / AMonitor.
 */
const UserMonitors = ({
  fixedFilter = null,
  periodDisabled = false,
  infoText = null,
  persistFilters = true
}) => {
  const law = useSelector((state) => state.user.session?.law);
  const canView = getLawFlag(law, 67) === 1;

  const [filters, setFilters] = useState(() =>
    persistFilters ? loadStoredFilters() : defaultPeriod()
  );
  const [debouncedQ, setDebouncedQ] = useState(() => (filters.q || "").trim());
  const [rows, setRows] = useState([]);
  const [lookups, setLookups] = useState({
    vids: {},
    cats: {},
    vidOptions: [],
    userOptions: []
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detailsRecord, setDetailsRecord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const requestIdRef = useRef(0);
  const skipRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listBodyRef = useRef(null);
  const loadPageRef = useRef(null);

  useEffect(() => {
    if (persistFilters) saveFilters(filters);
  }, [filters, persistFilters]);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQ((filters.q || "").trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [filters.q]);

  useEffect(() => {
    if (!canView) return;
    fetchUserMonitorLookups()
      .then((data) => setLookups(normalizeLookups(data)))
      .catch(() =>
        setLookups({ vids: {}, cats: {}, vidOptions: [], userOptions: [] })
      );
  }, [canView]);

  const vidOptionsForChips = useMemo(() => {
    const options = lookups.vidOptions || [];
    const from = fixedFilter?.vidFrom;
    const to = fixedFilter?.vidTo;
    if (from == null && to == null) return options;
    return options.filter(
      (o) =>
        (from == null || Number(o.value) >= from) &&
        (to == null || Number(o.value) <= to)
    );
  }, [lookups.vidOptions, fixedFilter?.vidFrom, fixedFilter?.vidTo]);

  const loadPage = useCallback(
    ({ reset }) => {
      if (!reset && (loadingMoreRef.current || !hasMoreRef.current)) return;

      const requestId = ++requestIdRef.current;
      const skip = reset ? 0 : skipRef.current;

      if (reset) {
        setLoading(true);
        setError("");
        if (listBodyRef.current) listBodyRef.current.scrollTop = 0;
      } else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      fetchUserMonitorList({
        dateBeg: filters.dateBeg,
        dateEnd: filters.dateEnd,
        vid: filters.vid,
        userName: filters.userName,
        q: debouncedQ,
        sortBy: filters.sortBy || "fdatetime",
        sortDir: filters.sortDir || "asc",
        idObjZag: fixedFilter?.idObjZag,
        vidFrom: fixedFilter?.vidFrom,
        vidTo: fixedFilter?.vidTo,
        skip,
        take: PAGE_SIZE
      })
        .then((data) => {
          if (requestId !== requestIdRef.current) return;
          const page = normalizePage(data);
          setRows((prev) => (reset ? page.items : [...prev, ...page.items]));
          skipRef.current = skip + page.items.length;
          hasMoreRef.current = page.hasMore;
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          if (reset) {
            setError(e.message || "Не удалось загрузить журнал");
            setRows([]);
            skipRef.current = 0;
            hasMoreRef.current = false;
          } else {
            message.error(e.message || "Не удалось подгрузить записи");
          }
        })
        .finally(() => {
          if (requestId !== requestIdRef.current) return;
          if (reset) setLoading(false);
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
    },
    [
      filters.dateBeg,
      filters.dateEnd,
      filters.vid,
      filters.userName,
      filters.sortBy,
      filters.sortDir,
      debouncedQ,
      fixedFilter?.idObjZag,
      fixedFilter?.vidFrom,
      fixedFilter?.vidTo
    ]
  );

  useEffect(() => {
    if (!canView) return;
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [
    canView,
    filters.dateBeg,
    filters.dateEnd,
    filters.vid,
    filters.userName,
    filters.sortBy,
    filters.sortDir,
    debouncedQ,
    fixedFilter?.idObjZag,
    fixedFilter?.vidFrom,
    fixedFilter?.vidTo,
    loadPage
  ]);

  loadPageRef.current = loadPage;

  useEffect(() => {
    const body = listBodyRef.current;
    if (!body) return undefined;

    const onScroll = () => {
      if (loadingMoreRef.current || !hasMoreRef.current) return;
      if (body.scrollTop + body.clientHeight >= body.scrollHeight - SCROLL_LOAD_GAP) {
        loadPageRef.current?.({ reset: false });
      }
    };

    body.addEventListener("scroll", onScroll);
    return () => body.removeEventListener("scroll", onScroll);
  }, [loading]);

  const reload = useCallback(() => {
    skipRef.current = 0;
    hasMoreRef.current = false;
    loadPage({ reset: true });
  }, [loadPage]);

  const openDetails = useCallback((record) => {
    if (!record) return;
    setSelectedId(record.idReg);
    setDetailsRecord(record);
    setDetailsOpen(true);
  }, []);

  const selectDetailsRecord = useCallback((record) => {
    if (!record) return;
    setSelectedId(record.idReg);
    setDetailsRecord(record);
  }, []);

  if (!canView) {
    return (
      <div className="user-monitors">
        <Alert type="warning" showIcon message="Нет права просмотра действий пользователей" />
      </div>
    );
  }

  const searchPlaceholder = infoText
    ? `Поиск — ${infoText.charAt(0).toLowerCase()}${infoText.slice(1)}`
    : "Поиск: пользователь, событие, объект, комментарий, №";
  const sortValue = `${filters.sortBy || "fdatetime"}:${filters.sortDir === "desc" ? "desc" : "asc"}`;

  return (
    <div className="user-monitors">
      <div className="user-monitors__filters">
        <div className="user-monitors__filters-row">
          <div className="user-monitors__field user-monitors__field--sort">
            <Select
              value={sortValue}
              options={SORT_OPTIONS}
              popupMatchSelectWidth={false}
              title="Сортировка"
              aria-label="Сортировка"
              onChange={(v) => {
                const [sortBy, sortDir] = String(v).split(":");
                setFilters((prev) => ({ ...prev, sortBy, sortDir }));
              }}
            />
          </div>
          <div className="user-monitors__field user-monitors__field--combo">
            <UserMonitorComboFilter
              value={{
                q: filters.q,
                vid: filters.vid,
                userName: filters.userName,
                dateBeg: filters.dateBeg,
                dateEnd: filters.dateEnd
              }}
              vidOptions={vidOptionsForChips}
              userOptions={lookups.userOptions}
              placeholder={searchPlaceholder}
              periodDisabled={periodDisabled}
              onChange={(patch) => setFilters((prev) => ({ ...prev, ...patch }))}
            />
          </div>
          <Tooltip title="Обновить">
            <button
              type="button"
              className="user-monitors__icon-btn"
              disabled={loading}
              aria-label="Обновить"
              onClick={reload}
            >
              <ReloadOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
      {error ? (
        <Alert className="user-monitors__error" type="error" showIcon message={error} />
      ) : null}
      <div ref={listBodyRef} className="user-monitors__body">
        {loading && rows.length === 0 ? (
          <div className="user-monitors__center">
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div className="user-monitors__center user-monitors__empty">
            Нет записей
          </div>
        ) : (
          <div className="user-monitors__cards">
            {rows.map((r) => (
              <UserMonitorCard
                key={r.idReg}
                record={r}
                catLabel={labelFromMap(lookups.cats, r.idCat, String(r.idCat))}
                vidLabel={labelFromMap(lookups.vids, r.vid, String(r.vid))}
                selected={r.idReg === selectedId}
                onSelect={openDetails}
              />
            ))}
            {loadingMore ? (
              <div className="user-monitors__cards-more">
                <Spin size="small" />
              </div>
            ) : null}
          </div>
        )}
      </div>
      <UserMonitorDetailsDrawer
        open={detailsOpen}
        record={detailsRecord}
        lookups={lookups}
        onClose={() => setDetailsOpen(false)}
        onSelectRecord={selectDetailsRecord}
      />
    </div>
  );
};

export default UserMonitors;
